import { PropsWithChildren, useEffect, useMemo } from 'react';
import { ConfigProvider, App as AntdApp } from 'antd';
import { AppThemeProvider } from '@digiwedge/ui-theme';
import { GlobalStateProvider } from '@digiwedge/global-state';
import { useI18n } from './i18n.js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

/**
 * Unifies AntD App, CSS-in-JS, Theme, and direction (LTR/RTL).
 */
export function PortalProviders({ children }: PropsWithChildren) {
  const { direction } = useI18n();

  const config = useMemo(() => ({ direction }), [direction]);
  const queryClient = useMemo(() => new QueryClient(), []);

  // Ensure document dir matches current direction for native elements
  useEffect(() => {
    try {
      document.documentElement.setAttribute('dir', direction);
    } catch {
      /* noop */
    }
  }, [direction]);

  return (
    <GlobalStateProvider>
      <ConfigProvider {...config}>
        <AntdApp>
          <QueryClientProvider client={queryClient}>
            <AppThemeProvider>{children}</AppThemeProvider>
          </QueryClientProvider>
        </AntdApp>
      </ConfigProvider>
    </GlobalStateProvider>
  );
}
